import type { RefObject } from "react";
import type { AgGridReact, ColDef, ColGroupDef } from "~/components/grid/AgGrid";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAgGridColumnCapabilitiesQuery } from "~/gql/queries/agGridColumnCapabilities.generated";
import applyGridColumnCapabilities, {
	COLUMNS_SORTED_ON_A_HIDDEN_VALUE
} from "~/helpers/grid/applyGridColumnCapabilities";
import {
	getServerGridId,
	setColumnCapabilityApplier,
	subscribeServerGridRegistered
} from "~/helpers/grid/serverGridRegistry";

interface GridColumnCapabilitiesApplier<TData> {
	apply: <T extends ColDef<TData> | ColGroupDef<TData>>(columns: T[]) => T[];
	hasAdjustedColumnDefs: boolean;
}

const hasColumnsSortedOnAHiddenValue = (gridId: string): boolean =>
	(COLUMNS_SORTED_ON_A_HIDDEN_VALUE[gridId]?.length ?? 0) > 0;

const useGridColumnCapabilities = <TData>(
	gridRef: RefObject<AgGridReact<TData> | null>,
	enabled: boolean
): GridColumnCapabilitiesApplier<TData> => {
	const { t } = useTranslation();
	const [gridId, setGridId] = useState<string>();
	useEffect(() => {
		if (!enabled) return;
		setGridId(getServerGridId(gridRef.current?.api));
		return subscribeServerGridRegistered((api, registeredGridId) => {
			if (api === gridRef.current?.api) setGridId(registeredGridId);
		});
	}, [enabled, gridRef]);
	const { data } = useAgGridColumnCapabilitiesQuery({ gridId: gridId ?? "" }, { skip: !enabled || !gridId });
	const capabilities = data?.agGridColumnCapabilities;
	const sortMessage = t("grid.sortUnsupported");
	const filterMessage = t("grid.filterUnsupported");
	const messages = useMemo(() => ({ filter: filterMessage, sort: sortMessage }), [filterMessage, sortMessage]);

	const apply = useCallback(
		<T extends ColDef<TData> | ColGroupDef<TData>>(columns: T[]): T[] =>
			enabled ? (applyGridColumnCapabilities(columns, { capabilities, gridId, messages }) as T[]) : columns,
		[capabilities, enabled, gridId, messages]
	);
	const hasAdjustedColumnDefs =
		enabled && gridId !== undefined && (capabilities !== undefined || hasColumnsSortedOnAHiddenValue(gridId));

	useEffect(() => {
		const api = gridRef.current?.api;
		if (!api || !hasAdjustedColumnDefs) return;
		setColumnCapabilityApplier(api, apply);
		const current = api.getColumnDefs();
		if (!current) return;
		const adjusted = apply(current);
		if (adjusted !== current) api.setGridOption("columnDefs", adjusted);
		return () => setColumnCapabilityApplier(api, undefined);
	}, [apply, gridRef, hasAdjustedColumnDefs]);

	return useMemo(() => ({ apply, hasAdjustedColumnDefs }), [apply, hasAdjustedColumnDefs]);
};

export default useGridColumnCapabilities;
